import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Download, TrendingUp, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const Delta = ({ change, pct, testid }) => {
  const up = change > 0;
  const flat = change === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = flat ? "text-slate-500" : up ? "text-green-800" : "text-red-800";
  return (
    <span className={`inline-flex items-center gap-0.5 num text-xs font-semibold ${tone}`} data-testid={testid}>
      <Icon size={13} />
      {change > 0 ? "+" : ""}{change}
      {pct !== null && pct !== undefined ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""}
    </span>
  );
};

const CompareTable = ({ title, rows, currentLabel, priorLabel, testid }) => (
  <div className="border border-slate-200" data-testid={testid}>
    <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
      {title}
    </div>
    {rows.length === 0 ? (
      <div className="p-4 text-sm text-slate-500">None recorded in either period.</div>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-1.5 text-left font-semibold">Item</th>
            <th className="px-3 py-1.5 text-right font-semibold">{currentLabel}</th>
            <th className="px-3 py-1.5 text-right font-semibold">{priorLabel}</th>
            <th className="px-3 py-1.5 text-right font-semibold">Shift</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 12).map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0" data-testid={`${testid}-row-${r.name}`}>
              <td className="px-3 py-1.5 text-slate-800">{r.name}</td>
              <td className="num px-3 py-1.5 text-right font-semibold text-slate-900">{r.current}</td>
              <td className="num px-3 py-1.5 text-right text-slate-500">{r.prior}</td>
              <td className="px-3 py-1.5 text-right"><Delta change={r.change} pct={r.pct} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Tile = ({ label, value, sub, testid }) => (
  <div className="border border-slate-200 p-3" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="num mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

const Breakdown = ({ title, rows, total, testid }) => (
  <div className="border border-slate-200" data-testid={testid}>
    <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
      {title}
    </div>
    {rows.length === 0 ? (
      <div className="p-4 text-sm text-slate-500">None recorded.</div>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0" data-testid={`${testid}-row-${r.name}`}>
              <td className="px-3 py-1.5 text-slate-800">{r.name}</td>
              <td className="num w-16 px-3 py-1.5 text-right font-semibold text-slate-900">{r.count}</td>
              <td className="w-28 py-1.5 pr-3">
                <div className="h-1.5 bg-slate-100">
                  <div className="h-1.5 bg-[#134e4a]" style={{ width: `${total ? (r.count / total) * 100 : 0}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function Statistics() {
  const [monthly, setMonthly] = useState(null);
  const [month, setMonth] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [yoy, setYoy] = useState(null);
  const [showYoy, setShowYoy] = useState(true);

  useEffect(() => {
    api.get("/stats/monthly", { params: { months: 12 } })
      .then((r) => {
        setMonthly(r.data);
        const withData = [...r.data.series].reverse().find((s) => s.cases > 0);
        setMonth(withData ? withData.month : "");
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const loadBreakdown = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/breakdown", { params: { month } });
      setBreakdown(data);
    } catch (e) { toast.error(apiError(e)); }
    if (!month) { setYoy(null); return; }
    try {
      const { data } = await api.get("/stats/yoy", { params: { month } });
      setYoy(data);
    } catch (e) { setYoy(null); toast.error(apiError(e)); }
  }, [month]);

  useEffect(() => { loadBreakdown(); }, [loadBreakdown]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/stats/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pathtrax_statistics_${month || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Statistics exported");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!monthly) return <div className="text-sm text-slate-500" data-testid="stats-loading">Loading statistics…</div>;
  const t = breakdown?.totals;

  return (
    <div className="space-y-5" data-testid="statistics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Monthly Statistics</h1>
          <p className="mt-1 text-sm text-slate-600">
            Case, block and slide volumes, tissue sources, cytology mix and where specimens originated.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <select
            value={month} onChange={(e) => setMonth(e.target.value)} data-testid="stats-month-select"
            className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="">All time</option>
            {[...monthly.series].reverse().map((s) => (
              <option key={s.month} value={s.month}>{s.label}{s.cases ? ` (${s.cases})` : ""}</option>
            ))}
          </select>
          <Button variant="outline" onClick={exportCsv} data-testid="stats-export-button" className="h-11">
            <Download size={15} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="stats-tiles">
        <Tile label="Cases" value={t?.cases ?? 0} testid="stat-cases" />
        <Tile label="Blocks" value={t?.blocks ?? 0} sub={`${t?.blocks_per_case ?? 0} / case`} testid="stat-blocks" />
        <Tile label="Slides" value={t?.slides ?? 0} sub={`${t?.slides_per_case ?? 0} / case`} testid="stat-slides" />
        <Tile label="Cytology" value={t?.cytology_cases ?? 0} testid="stat-cytology" />
        <Tile label="STAT" value={t?.stat_cases ?? 0} testid="stat-stat" />
        <Tile label="Signed out" value={t?.signed_out ?? 0} testid="stat-signed-out" />
        <Tile label="Avg TAT" value={`${t?.avg_tat_hours ?? 0}h`} testid="stat-avg-tat" />
        <Tile label="Send-outs" value={t?.sendouts ?? 0} testid="stat-sendouts" />
      </div>

      <div className="border border-slate-200" data-testid="stats-chart-panel">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <TrendingUp size={14} /> 12-month volume — {monthly.totals.cases} cases vs {monthly.totals.prior_cases} in the prior year
          </div>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <input
              type="checkbox" checked={showYoy} onChange={(e) => setShowYoy(e.target.checked)}
              data-testid="toggle-yoy-overlay" className="h-4 w-4 accent-[#134e4a]"
            />
            Overlay last year
          </label>
        </div>
        <div className="h-72 p-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly.series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #cbd5e1", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="cases" name="Cases" fill="#134e4a" />
              {showYoy && <Bar dataKey="prior_cases" name="Cases (same month last year)" fill="#cbd5e1" />}
              <Bar dataKey="blocks" name="Blocks" fill="#0f766e" />
              <Bar dataKey="slides" name="Slides" fill="#99f6e4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[860px] text-sm" data-testid="stats-monthly-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">Month</th>
              <th className="px-3 py-2 text-right">Cases</th>
              <th className="px-3 py-2 text-right">Last year</th>
              <th className="px-3 py-2 text-right">Shift</th>
              <th className="px-3 py-2 text-right">Surgical</th>
              <th className="px-3 py-2 text-right">Cytology</th><th className="px-3 py-2 text-right">STAT</th>
              <th className="px-3 py-2 text-right">Blocks</th><th className="px-3 py-2 text-right">Slides</th>
              <th className="px-3 py-2 text-right">Signed out</th><th className="px-3 py-2 text-right">Avg TAT</th>
            </tr>
          </thead>
          <tbody>
            {[...monthly.series].reverse().map((s) => (
              <tr
                key={s.month} onClick={() => setMonth(s.month)} data-testid={`monthly-row-${s.month}`}
                className={`cursor-pointer border-b border-slate-100 row-hover hover:bg-slate-50 ${month === s.month ? "bg-[#134e4a]/5" : ""}`}
              >
                <td className="px-3 py-2 font-medium text-slate-800">{s.label}</td>
                <td className="num px-3 py-2 text-right font-semibold">{s.cases}</td>
                <td className="num px-3 py-2 text-right text-slate-500" data-testid={`monthly-prior-${s.month}`}>{s.prior_cases}</td>
                <td className="px-3 py-2 text-right"><Delta change={s.cases_change} pct={s.cases_pct} /></td>
                <td className="num px-3 py-2 text-right text-slate-600">{s.surgical}</td>
                <td className="num px-3 py-2 text-right text-slate-600">{s.cytology}</td>
                <td className="num px-3 py-2 text-right text-slate-600">{s.stat_cases}</td>
                <td className="num px-3 py-2 text-right">{s.blocks}</td>
                <td className="num px-3 py-2 text-right">{s.slides}</td>
                <td className="num px-3 py-2 text-right text-slate-600">{s.signed_out}</td>
                <td className="num px-3 py-2 text-right text-slate-600">{s.avg_tat_hours}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {yoy && (
        <div className="space-y-4" data-testid="yoy-panel">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900" data-testid="yoy-heading">
              Year over year — {yoy.current.label} vs {yoy.prior.label}
            </h2>
            <span className="text-sm text-slate-500">same month, one year earlier</span>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="yoy-tiles">
            {[
              ["Cases", "cases"], ["Blocks", "blocks"], ["Slides", "slides"], ["Cytology", "cytology_cases"],
              ["STAT", "stat_cases"], ["Signed out", "signed_out"], ["Avg TAT", "avg_tat_hours"], ["Send-outs", "sendouts"],
            ].map(([label, key]) => {
              const d = yoy.deltas[key];
              const suffix = key === "avg_tat_hours" ? "h" : "";
              return (
                <div key={key} className="border border-slate-200 p-3" data-testid={`yoy-tile-${key}`}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                  <div className="num mt-1 font-display text-2xl font-bold text-slate-900">{d.current}{suffix}</div>
                  <div className="num text-xs text-slate-500">was {d.prior}{suffix}</div>
                  <div className="mt-1"><Delta change={d.change} pct={d.pct} testid={`yoy-delta-${key}`} /></div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <CompareTable title="Specimen class shift" rows={yoy.sections.specimen_classes} currentLabel={yoy.current.label} priorLabel={yoy.prior.label} testid="yoy-classes" />
            <CompareTable title="Tissue source shift" rows={yoy.sections.tissue_sources} currentLabel={yoy.current.label} priorLabel={yoy.prior.label} testid="yoy-tissue" />
            <CompareTable title="Cytology type shift" rows={yoy.sections.cytology_types} currentLabel={yoy.current.label} priorLabel={yoy.prior.label} testid="yoy-cytology" />
            <CompareTable title="Origin shift" rows={yoy.sections.origins} currentLabel={yoy.current.label} priorLabel={yoy.prior.label} testid="yoy-origins" />
            <CompareTable title="Ordering physician shift" rows={yoy.sections.ordering_physicians} currentLabel={yoy.current.label} priorLabel={yoy.prior.label} testid="yoy-physicians" />
          </div>
        </div>
      )}

      {breakdown && (
        <>
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900" data-testid="breakdown-heading">
            Breakdown — {breakdown.label}
          </h2>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <Breakdown title="Specimen class" rows={breakdown.specimen_classes} total={t?.cases} testid="breakdown-classes" />
            <Breakdown title="Tissue sources (specimen type)" rows={breakdown.tissue_sources} total={t?.cases} testid="breakdown-tissue" />
            <Breakdown title="Cytology specimen types" rows={breakdown.cytology_types} total={t?.cytology_cases} testid="breakdown-cytology" />
            <Breakdown title="Where specimens originated" rows={breakdown.origins} total={t?.cases} testid="breakdown-origins" />
            <Breakdown title="Specimen sites" rows={breakdown.specimen_sites} total={t?.cases} testid="breakdown-sites" />
            <Breakdown title="Ordering physicians" rows={breakdown.ordering_physicians} total={t?.cases} testid="breakdown-physicians" />
            <Breakdown title="Stains used" rows={breakdown.stains} total={t?.slides} testid="breakdown-stains" />
            <Breakdown title="Send-out reference labs" rows={breakdown.sendout_labs} total={t?.sendouts} testid="breakdown-sendout-labs" />
          </div>
        </>
      )}
    </div>
  );
}
